// PaymentDialog.js - Enhanced Version with Conditional Payment Methods Display and Debt Payment Support
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  LinearProgress,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon,
  Payments as PaymentsIcon,
  AccessTime as AccessTimeIcon,
  AccountBalance as DebtIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { formatCurrency, successMessage } from '../Message';
import { initiatepayment, checkpaymentstatus, PaymentProcess, facilitySettingsAPI } from '../BaseURL';
import MomoPaymentDialog from './MomoPaymentDialog';

const PaymentDialog = ({
  open,
  onClose,
  selectedTotal,
  selectedPatient,
  selectedCharges,
  onPaymentSuccess,
  currentUser,
  serviceCharges,
  investigationCharges,
  medicationCharges,
  otherCharges,
  // New props for conditional display
  showPaymentMethods = true, // Show/hide payment methods section
  allowedPaymentMethods = ['cash', 'mobile_money', 'credit'], // Array of allowed methods
  defaultPaymentMethod = 'cash', // Default selected method
  enableMobileMoneyValidation = true, // Enable/disable mobile money validation
  showSecurityNotice = true, // Show/hide security notice
  // New parameter for debt payment
  isDebtPayment = false, // Flag to indicate if this is a debt payment
  debtDetails = null, // Object containing debt details like debtId, originalAmount, remainingAmount, etc.
  paymentType = 'normal' // 'normal', 'debt', 'partial_debt'
}) => {
  // ── Theme tokens ──────────────────────────────────────────────
  // The dialog used to bake light pastels into every panel (white
  // gradients, #ebf4ff, #fef3c7, etc.) which became unreadable on the
  // black theme. These tokens swap surfaces, text and accents to suit
  // both themes from a single render path.
  const muiTheme = useTheme();
  const t = useMemo(() => {
    const isDark = muiTheme.palette.mode === 'dark';
    return {
      isDark,
      pageBg:       isDark ? '#000000' : '#F4F6FA',
      surface:      isDark ? '#0E0E14' : '#FFFFFF',
      surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
      fieldBg:      isDark ? '#14141C' : '#FFFFFF',
      borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
      borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
      text:         isDark ? '#FFFFFF' : '#0F172A',
      textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
      textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
      accent: {
        primary: '#2563eb', // brand blue
        emerald: '#10b981',
        amber:   '#f59e0b',
        danger:  '#ef4444',
        green:   '#22c55e',
      }
    };
  }, [muiTheme.palette.mode]);

  const [paymentMethod, setPaymentMethod] = useState(defaultPaymentMethod);
  const [mobileMoneyData, setMobileMoneyData] = useState({
    payeeMobileNumber: '',
    channel: ''
  });
  const [mobileMoneyLoading, setMobileMoneyLoading] = useState(false);
  const [mobileMoneyError, setMobileMoneyError] = useState('');
  const [mobileMoneyDigits, setMobileMoneyDigits] = useState(Array(10).fill(''));
  const [statusCheckProgress, setStatusCheckProgress] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');
  
  // Refs for cleanup
  const statusCheckIntervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // ── Zenith GlobalPay MoMo integration ────────────────────────────────
  // Runs alongside the existing mobile_money flow. Gated on a facility
  // toggle so only facilities with a Zenith merchant account see it —
  // defaults OFF, admin flips it on from Module Toggles.
  const [zenithMomoEnabled, setZenithMomoEnabled] = useState(false);
  const [zenithDialogOpen,  setZenithDialogOpen]  = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(facilitySettingsAPI, { timeout: 8000 });
        if (!alive) return;
        const row = data?.success && Array.isArray(data.results)
          ? data.results.find((r) => r.key === 'zenith_momo_enabled')
          : null;
        if (row) {
          const s = String(row.value ?? '').trim().toLowerCase();
          setZenithMomoEnabled(s === 'true' || s === '1' || s === 'yes' || s === 'on');
        }
      } catch (_) { /* leave OFF */ }
    })();
    return () => { alive = false; };
  }, []);

  // When Zenith reports PAID, run the existing "mark charges paid +
  // day_income insert" flow — same shape as any other mobile-money
  // payment. Downstream reports (cashier tiles, PaymentMethods chart)
  // count it as Mobile Money automatically.
  const handleZenithPaid = useCallback(async () => {
    setZenithDialogOpen(false);
    // Delegate to the existing PaymentProcess PUT — sends
    // paymentMethod='mobile_money' so day_income picks up mode='Cash'
    // or 'Mobile Money' depending on the mapping in PaymentProcess.
    try {
      const {
        selectedServiceCharges,
        selectedInvestigationCharges,
        selectedMedicationCharges,
        selectedOtherCharges,
      } = getSelectedCharges();

      const payload = {
        paymentMethod: 'mobile_money',
        selectedPatient,
        currentUser,
        selectedServiceCharges,
        selectedInvestigationCharges,
        selectedMedicationCharges,
        selectedOtherCharges,
        selectedTotal,
        patient_name: selectedPatient?.fullName,
        tel:          selectedPatient?.tel,
        res:          selectedPatient?.res,
        id:           selectedPatient?.id,
        gender:       selectedPatient?.gender,
        age:          selectedPatient?.age,
        isDebtPayment,
        paymentType,
        debtDetails: isDebtPayment ? { ...debtDetails } : null,
      };
      const { data } = await axios.put(PaymentProcess, payload);
      if (data?.success) {
        await Swal.fire({
          title: 'Payment Applied',
          text: 'Zenith MoMo payment recorded and bill updated.',
          icon: 'success',
          timer: 2200, showConfirmButton: false,
        });
        if (onPaymentSuccess) onPaymentSuccess();
        onClose();
      } else {
        await Swal.fire({
          icon: 'warning',
          title: 'Payment received, bill NOT updated',
          text: (data?.message || 'PaymentProcess did not confirm the update. Check the bill manually.') +
                ' The MoMo transaction succeeded and the money is in — this only affects the local bill records.',
        });
      }
    } catch (err) {
      await Swal.fire({
        icon: 'warning',
        title: 'Payment received, bill NOT updated',
        text: (err.response?.data?.message || err.message) +
              '. The MoMo transaction succeeded; you can manually reconcile the bill.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, selectedTotal, currentUser, isDebtPayment, paymentType, debtDetails, onPaymentSuccess, onClose]);

  const mobileMoneyChannels = [
    { id: 'mtn-gh', label: 'MTN', icon: '📱' },
    { id: 'vodafone-gh', label: 'Telecel', icon: '📱' },
    { id: 'tigo-gh', label: 'AirtelTigo', icon: '📱' }
  ];

  
  const allPaymentMethods = [
    { 
      id: 'cash', 
      label: 'Cash Payment', 
      icon: PaymentsIcon,
      description: isDebtPayment ? 'Pay debt with physical cash' : 'Pay with physical cash'
    },
    { 
      id: 'mobile_money', 
      label: 'Mobile Money', 
      icon: PhoneIcon,
      description: isDebtPayment ? 'Pay debt via MTN, Vodafone, AirtelTigo' : 'Pay via MTN, Vodafone, AirtelTigo'
    },

     {
      id: 'mobile_money(manual)',
      label: 'Mobile Money(manual)',
      icon: PhoneIcon,
      description: isDebtPayment ? 'Pay debt via MTN, Vodafone, AirtelTigo (mobile_money(manual))' : 'Pay via MTN, Vodafone, AirtelTigo'
    },

    // Zenith GlobalPay MoMo — surfaced only when the facility toggle
    // `zenith_momo_enabled` is on. Adds a new payment option that
    // sends a real debit prompt to the customer's phone via Zenith's
    // /paymentsMomo/initiate route and polls until PAID.
    {
      id: 'zenith_momo',
      label: 'Mobile Money (Zenith)',
      icon: PhoneIcon,
      description: isDebtPayment
        ? 'Pay debt via Zenith GlobalPay (real MoMo debit)'
        : 'Send a debit prompt via Zenith GlobalPay (MTN / Vodafone / AirtelTigo)',
    },

    {
      id: 'credit',
      label: 'Credit Bill',
      icon: CreditCardIcon,
      description:  'Credit Patient Bill'
    }
  ];

  // Filter payment methods based on allowedPaymentMethods prop.
  // The Zenith option is also gated on the facility toggle so it stays
  // invisible to sites that haven't opted in, regardless of what the
  // parent passes in allowedPaymentMethods.
  const paymentMethods = allPaymentMethods.filter(method => {
    if (method.id === 'zenith_momo') {
      return zenithMomoEnabled && allowedPaymentMethods.includes('zenith_momo');
    }
    return allowedPaymentMethods.includes(method.id);
  });

  // Helper function to get selected charges (same as handleRegularPayment)
  const getSelectedCharges = () => {
    const selectedServiceCharges = serviceCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );
    
    const selectedInvestigationCharges = investigationCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );
    
    const selectedMedicationCharges = medicationCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );
    
    const selectedOtherCharges = otherCharges.filter(charge => 
      selectedCharges.includes(charge.id) && charge.status !== 'paid' && charge.status !== 'credit'
    );

    return {
      selectedServiceCharges,
      selectedInvestigationCharges,
      selectedMedicationCharges,
      selectedOtherCharges
    };
  };

  // Helper function to format phone number to international format
  const formatToInternational = (number) => {
    const cleanNumber = number.replace(/\D/g, '');
    
    if (cleanNumber.startsWith('233')) {
      return cleanNumber;
    }
    
    if (cleanNumber.startsWith('0')) {
      return '233' + cleanNumber.substring(1);
    }
    
    return '233' + cleanNumber;
  };

  // Enhanced status checking with visual feedback
  const performStatusCheck = async (clientReference, maxAttempts = 12, attemptInterval = 10000) => {
    let attempts = 0;
    setIsCheckingStatus(true);
    setStatusCheckProgress(0);
    
    return new Promise((resolve) => {
      const checkStatus = async () => {
        attempts++;
        const progress = (attempts / maxAttempts) * 100;
        setStatusCheckProgress(progress);
        
        try {
          setPaymentStatusMessage(`Checking payment status... (${attempts}/${maxAttempts})`);
          
          const statusResult = await checkPaymentStatus(clientReference);
          
          if (statusResult.success && statusResult.status === 'success') {
            // Payment successful
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            setStatusCheckProgress(100);
            setPaymentStatusMessage('Payment verified successfully!');
            
            const paymentTypeText = isDebtPayment ? 'Debt Payment' : 'Payment';
            await Swal.fire({
              title: `${paymentTypeText} Successful! 🎉`,
              text: `${paymentTypeText} of ${formatCurrency(selectedTotal)} has been confirmed.`,
              icon: 'success',
              confirmButtonText: 'Great!',
              timer: 5000
            });
            
            if (onPaymentSuccess) {
              onPaymentSuccess();
            }
            
            resolve({ success: true, status: 'success' });
            return;
          }
          
          if (statusResult.success && statusResult.status === 'failed') {
            // Payment failed
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            
            await Swal.fire({
              title: 'Payment Failed',
              text: `The mobile money ${isDebtPayment ? 'debt ' : ''}payment was not completed. Please try again.`,
              icon: 'error',
              confirmButtonText: 'OK'
            });
            
            resolve({ success: false, status: 'failed' });
            return;
          }
          
          // Still pending, continue checking if we haven't exceeded max attempts
          if (attempts >= maxAttempts) {
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            
            await Swal.fire({
              title: 'Payment Status Unknown',
              html: `
                <p>We couldn't confirm your ${isDebtPayment ? 'debt ' : ''}payment status automatically.</p>
                <p><strong>What to do:</strong></p>
                <ul style="text-align: left; margin: 10px 0;">
                  <li>Check your mobile money balance</li>
                  <li>If money was deducted, contact support with reference: <strong>${clientReference}</strong></li>
                  <li>If no money was deducted, you can try again</li>
                </ul>
              `,
              icon: 'warning',
              confirmButtonText: 'I Understand',
              allowOutsideClick: false
            });
            
            resolve({ success: false, status: 'timeout' });
          }
        } catch (error) {
          console.error('Status check error:', error);
          // Continue checking unless max attempts reached
          if (attempts >= maxAttempts) {
            clearInterval(statusCheckIntervalRef.current);
            setIsCheckingStatus(false);
            resolve({ success: false, status: 'error', error: error.message });
          }
        }
      };
      
      // Start status checking immediately
      checkStatus();
      
      // Set up interval for subsequent checks
      statusCheckIntervalRef.current = setInterval(checkStatus, attemptInterval);
    });
  };

  const handleMobileMoneyChannelChange = useCallback((e) => {
    setMobileMoneyData(prev => ({ 
      ...prev, 
      channel: e.target.value 
    }));
  }, []);

  const handleMobileMoneyPayment = async () => {
    const joinedNumber = mobileMoneyDigits.join('');

    // Input validation (only if validation is enabled)
    if (enableMobileMoneyValidation) {
      if (joinedNumber.length !== 10) {
        setMobileMoneyError('Please enter a valid 10-digit number');
        return;
      }

      if (!mobileMoneyData.channel) {
        setMobileMoneyError('Please select a mobile money provider');
        return;
      }
    }

    // Validate amount
    if (!selectedTotal || selectedTotal <= 0) {
      setMobileMoneyError('Invalid payment amount');
      return;
    }

    // Validate patient data
    if (!selectedPatient || !selectedPatient.id) {
      setMobileMoneyError('Patient information is missing');
      return;
    }

    setMobileMoneyLoading(true);
    setMobileMoneyError('');
    
    try {
      // Get selected charges (same structure as handleRegularPayment)
      const {
        selectedServiceCharges,
        selectedInvestigationCharges,
        selectedMedicationCharges,
        selectedOtherCharges
      } = getSelectedCharges();

      // Prepare payment description based on payment type
      let paymentDescription = `Hospital Bill - Patient: ${selectedPatient.fullName || selectedPatient.name}`;
      if (isDebtPayment) {
        paymentDescription = `Debt Payment - Patient: ${selectedPatient.fullName || selectedPatient.name}`;
        if (debtDetails?.debtId) {
          paymentDescription += ` (Debt ID: ${debtDetails.debtId})`;
        }
      }

      // Prepare payment payload with selected charges and debt information
      const paymentPayload = {
        totalAmount: parseFloat(selectedTotal),
        description: paymentDescription,
        patientId: selectedPatient.id,
        patientName: (selectedPatient.fullName || selectedPatient.name || '').trim(),
        patientEmail: selectedPatient.email || "patient@hospital.com",
        patientPhone: formatToInternational(joinedNumber),
        billItems: isDebtPayment ? "Debt Payment" : "Hospital Services",
        // Add selected charges data for backend processing
        selectedCharges: {
          selectedServiceCharges,
          selectedInvestigationCharges,
          selectedMedicationCharges,
          selectedOtherCharges
        },
        // Additional patient info
        patient_name: selectedPatient.fullName,
        tel: selectedPatient.tel,
        res: selectedPatient.res,
        // Payment method and user info
        paymentMethod: 'mobile_money',
        currentUser,
        // New debt payment fields
        isDebtPayment,
        paymentType,
        debtDetails: isDebtPayment ? {
          debtId: debtDetails?.debtId || null,
          originalAmount: debtDetails?.originalAmount || selectedTotal,
          remainingAmount: debtDetails?.remainingAmount || selectedTotal,
          debtDate: debtDetails?.debtDate || null,
          debtDescription: debtDetails?.debtDescription || null,
          ...debtDetails
        } : null
      };
      
      console.log('Sending mobile money payment request:', paymentPayload);

      // Make the API call to initiate payment
      const response = await axios.post(initiatepayment, paymentPayload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000
      });

      console.log('Payment API Response:', response.data);

      // Handle successful response
      if (response.data.success) {
        const clientReference = response.data.data?.clientReference;
        
        if (!clientReference) {
          throw new Error('No client reference received from payment gateway');
        }

        // Show initial success message with debt payment context
        const paymentTypeText = isDebtPayment ? 'debt payment' : 'payment';
        await Swal.fire({
          title: `${isDebtPayment ? 'Debt ' : ''}Payment Request Sent! 📱`,
          html: `
            <div style="text-align: center;">
              <p>A mobile money ${paymentTypeText} prompt has been sent to <strong>${joinedNumber}</strong></p>
              <p style="color: #e67e22; font-weight: bold;">Please check your phone and complete the ${paymentTypeText}</p>
              <div style="background: #fff3cd; padding: 15px; border-radius: 8px; margin: 15px 0;">
                <p style="margin: 0; font-size: 14px;">
                  <strong>Steps to complete:</strong><br>
                  1. Check your phone for the payment prompt<br>
                  2. Enter your mobile money PIN<br>
                  3. Confirm the ${paymentTypeText}<br>
                  4. Wait for verification
                </p>
              </div>
              ${isDebtPayment && debtDetails?.debtId ? 
                `<p style="font-size: 12px; color: #666;">Debt ID: <strong>${debtDetails.debtId}</strong></p>` : ''
              }
              <p style="font-size: 12px; color: #666;">
                Reference: <strong>${clientReference}</strong>
              </p>
            </div>
          `,
          icon: 'info',
          confirmButtonText: `I've Completed ${isDebtPayment ? 'Debt ' : ''}Payment`,
          timer: 15000,
          allowOutsideClick: false
        });
        
        // Start automated status checking
        setMobileMoneyLoading(false);
        const statusResult = await performStatusCheck(clientReference);
        
        if (statusResult.success && statusResult.status === 'success') {
          onClose();
        }
        
      } else {
        // Handle API error response
        const errorMessage = response.data.message || `Failed to initialize ${isDebtPayment ? 'debt ' : ''}payment`;
        setMobileMoneyError(errorMessage);
        console.error('API Error:', response.data);
      }

    } catch (error) {
      console.error('Mobile Money payment initialization error:', error);
      
      // Handle different types of errors
      if (error.code === 'ECONNABORTED') {
        setMobileMoneyError('Request timeout. Please check your internet connection and try again.');
      } else if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const errorData = error.response.data;
        
        console.error('Error Response:', {
          status,
          data: errorData
        });
        
        if (status === 500) {
          console.error('Server Error Details:', {
            message: errorData?.message,
            error: errorData?.error,
            stack: errorData?.stack
          });
          setMobileMoneyError('Server error occurred. Please contact support if this persists.');
        } else if (status === 403) {
          setMobileMoneyError('Access denied. IP address may not be whitelisted with payment gateway.');
        } else if (status === 400) {
          setMobileMoneyError(errorData?.message || 'Invalid request. Please check your input and try again.');
        } else {
          setMobileMoneyError(errorData?.message || `${isDebtPayment ? 'Debt p' : 'P'}ayment request failed (${status})`);
        }
      } else if (error.request) {
        setMobileMoneyError('No response from payment gateway. Please check your connection.');
      } else {
        setMobileMoneyError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setMobileMoneyLoading(false);
    }
  };

  const checkPaymentStatus = async (clientReference) => {
    try {
      // Validate client reference
      if (!clientReference || typeof clientReference !== 'string') {
        throw new Error('Invalid client reference provided');
      }

      console.log(`Checking payment status for client reference: ${clientReference}`);

      // Make request to backend endpoint
      const response = await axios.get(
        checkpaymentstatus(clientReference),
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 15000, // Shorter timeout for status checks
          validateStatus: function (status) {
            return (status >= 200 && status < 300) || status === 404;
          }
        }
      );

      // Handle different response scenarios
      if (response.status === 404) {
        console.warn(`Payment with client reference ${clientReference} not found`);
        return {
          success: false,
          status: 'not_found',
          message: 'Payment record not found',
          clientReference: clientReference
        };
      }

      // Successful response
      const paymentData = response.data;
      console.log('Payment status response:', paymentData);

      // Check if the backend request was successful
      if (!paymentData.success) {
        return {
          success: false,
          status: 'error',
          message: paymentData.message || 'Failed to check payment status',
          clientReference: clientReference,
          error: paymentData.error
        };
      }

      // Extract status from backend response
      const statusData = paymentData.data;
      let normalizedStatus = 'pending';
      
      if (statusData.status === 'Paid' || statusData.status === 'paid' || statusData.status === 'success') {
        normalizedStatus = 'success';
      } else if (statusData.status === 'Failed' || statusData.status === 'failed' || statusData.status === 'error') {
        normalizedStatus = 'failed';
      } else if (statusData.status === 'Pending' || statusData.status === 'pending') {
        normalizedStatus = 'pending';
      }

      // Return standardized response
      return {
        success: true,
        status: normalizedStatus,
        message: `Payment status: ${statusData.status}`,
        clientReference: clientReference,
        data: {
          transactionId: statusData.transactionId,
          externalTransactionId: statusData.externalTransactionId,
          amount: statusData.amount,
          paymentMethod: statusData.paymentMethod,
          date: statusData.date,
          charges: statusData.charges,
          amountAfterCharges: statusData.amountAfterCharges
        }
      };

    } catch (error) {
      console.error('Payment status check error:', error);

      // Handle network/timeout errors
      if (error.code === 'ECONNABORTED') {
        return {
          success: false,
          status: 'timeout',
          message: 'Request timeout - please try again',
          clientReference: clientReference
        };
      }

      // Handle HTTP errors
      if (error.response) {
        const status = error.response.status;
        const statusText = error.response.statusText;
        
        console.error(`HTTP ${status}: ${statusText}`);
        
        if (status === 404) {
          return {
            success: false,
            status: 'not_found',
            message: 'Payment record not found',
            clientReference: clientReference
          };
        } else if (status === 403) {
          return {
            success: false,
            status: 'forbidden',
            message: 'Access forbidden - please contact support',
            clientReference: clientReference
          };
        } else {
          return {
            success: false,
            status: 'error',
            message: `HTTP ${status}: ${statusText}`,
            clientReference: clientReference,
            errorCode: status
          };
        }
      }

      // Handle other errors
      return {
        success: false,
        status: 'error',
        message: error.message || 'Unknown error occurred',
        clientReference: clientReference
      };
    }
  };

 
  const handleRegularPayment = async () => {
  const {
    selectedServiceCharges,
    selectedInvestigationCharges,
    selectedMedicationCharges,
    selectedOtherCharges
  } = getSelectedCharges();

  try {
    const loadingText = isDebtPayment ? 'Processing Debt Payment.......' : 'Processing.......';
    Swal.fire({
      title: loadingText,
      text: 'Please wait',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => { Swal.showLoading(); }
    });

    const patient_name = selectedPatient.fullName;
    const tel          = selectedPatient.tel;
    const res          = selectedPatient.res;
    const id           = selectedPatient.id;
    const gender       = selectedPatient.gender;
    const age          = selectedPatient.age;

    const paymentPayload = {
      paymentMethod,
      selectedPatient,
      currentUser,
      selectedServiceCharges,
      selectedInvestigationCharges,
      selectedMedicationCharges,
      selectedOtherCharges,
      selectedTotal,
      patient_name,
      tel,
      res,
      id,
      gender,
      age,
      isDebtPayment,
      paymentType,
      debtDetails: isDebtPayment ? { ...debtDetails } : null
    };

    const response = await axios.put(PaymentProcess, paymentPayload);

    if (response.data.success) {
      const successText = isDebtPayment
        ? 'Debt Payment Successfully Processed'
        : 'Payment Successfully Processed';
      successMessage('Save', successText);
      if (onPaymentSuccess) onPaymentSuccess();
      onClose();
    }

  } catch (error) {
    Swal.close(); // close loading spinner

    const responseData = error.response?.data;

    // ── ★ Handle out-of-stock expired medication ──────────────────────────
    if (responseData?.isStockError && responseData?.message?.startsWith('OUT_OF_STOCK::')) {
      const parts   = responseData.message.split('::');
      const medName = parts[1] || 'Unknown medication';
      const detail  = parts[2] || '';

      await Swal.fire({
        title: '⚠️ Medication Out of Stock',
        html: `
          <div style="text-align:left; padding: 8px 0;">
            <p style="margin-bottom:12px;">
              The following medication was <strong>previously reserved</strong> but the 
              reservation expired before payment was made. It has since run out of stock 
              at the dispensary.
            </p>
            <div style="
              background:#fff3e0; 
              border:1px solid #f59e0b; 
              border-left:4px solid #f59e0b;
              border-radius:8px; 
              padding:14px 16px;
              margin-bottom:12px;">
              <p style="font-weight:700; font-size:1rem; color:#92400e; margin:0 0 6px;">
                ${medName}
              </p>
              <p style="font-size:0.85rem; color:#78350f; margin:0;">
                ${detail}
              </p>
            </div>
            <p style="color:#555; font-size:0.9rem;">
              <strong>What to do:</strong><br/>
              • Ask the doctor to re-prescribe once the drug is restocked<br/>
              • The pharmacy can notify the store for an urgent restock<br/>
              • You can proceed with paying all other charges now
            </p>
          </div>
        `,
        icon: 'warning',
        confirmButtonText: 'Understood',
        confirmButtonColor: '#f59e0b',
        width: '500px'
      });

      return; // stop — do NOT close the payment dialog, let cashier deselect the item
    }

    // ── Generic error ─────────────────────────────────────────────────────
    await Swal.fire({
      title: 'Error',
      text: responseData?.message || 'Failed to Process Payment',
      icon: 'error'
    });
  }
};


  const handlePaymentSubmit = async () => {
  // Get readable payment method name
  const selectedMethodLabel = (() => {
    switch (paymentMethod) {
      case 'cash':
        return 'Cash Payment';
      case 'mobile_money':
        return 'Mobile Money';
      case 'mobile_money(manual)':
        return 'Mobile Money (Manual)';
      case 'credit':
        return 'Credit Bill';
      default:
        return paymentMethod;
    }
  })();

  // Show confirmation popup before proceeding
  const { isConfirmed } = await Swal.fire({
    title: 'Confirm Payment',
    html: `
      <div style="text-align:center;">
        <p>Are you sure you want to process this ${
          isDebtPayment ? 'debt ' : ''
        }payment?</p>
        <p style="font-weight:600; color:#2563eb; margin-top:8px;">
          Amount: ${formatCurrency(selectedTotal)}
        </p>
        <p style="margin:4px 0;">
          Patient: <strong>${selectedPatient.fullName || selectedPatient.name}</strong>
        </p>
        <p style="margin:4px 0; color:#374151;">
          Payment Method: <strong>${selectedMethodLabel}</strong>
        </p>
      </div>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes, Process Payment',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    focusCancel: true,
  });

  if (isConfirmed) {
    // Proceed with payment
    if (paymentMethod === 'mobile_money') {
      await handleMobileMoneyPayment();
    } else if (paymentMethod === 'zenith_momo') {
      // Open the Zenith dialog — the actual wallet debit + status
      // polling lives in there. On PAID the dialog calls back into
      // handleZenithPaid which runs the normal charge-marking flow.
      setZenithDialogOpen(true);
    } else {
      await handleRegularPayment();
    }
  } else {
    // User cancelled payment
    Swal.fire({
      title: 'Payment Cancelled',
      text: 'The payment process has been cancelled.',
      icon: 'info',
      timer: 3000,
      showConfirmButton: false,
    });
  }
};


  const handleClose = () => {
    // Clear any ongoing status checks
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset form state
    setPaymentMethod(defaultPaymentMethod);
    setMobileMoneyData({ payeeMobileNumber: '', channel: '' });
    setMobileMoneyError('');
    setMobileMoneyLoading(false);
    setMobileMoneyDigits(Array(10).fill(''));
    setIsCheckingStatus(false);
    setStatusCheckProgress(0);
    setPaymentStatusMessage('');
    onClose();
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Validate if current payment method is allowed
  const isCurrentMethodAllowed = allowedPaymentMethods.includes(paymentMethod);

  // Auto-select first allowed method if current is not allowed
  React.useEffect(() => {
    if (!isCurrentMethodAllowed && paymentMethods.length > 0) {
      setPaymentMethod(paymentMethods[0].id);
    }
  }, [allowedPaymentMethods, paymentMethods, isCurrentMethodAllowed]);

  if (!selectedPatient) return null;

  // Determine dialog title and icon based on payment type
  const dialogTitle = isDebtPayment ? 'Process Debt Payment' : 'Process Payment';
  const dialogIcon = isDebtPayment ? DebtIcon : CreditCardIcon;
  const dialogSubtitle = isDebtPayment ? 'Secure debt payment processing' : 'Secure payment processing';

  const accent = isDebtPayment ? t.accent.amber : t.accent.primary;

  return (
    <>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isCheckingStatus}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: t.pageBg,
            backgroundImage: 'none',
            border: t.isDark ? `1px solid ${t.borderSubtle}` : 'none',
            boxShadow: t.isDark
              ? '0 24px 64px rgba(0,0,0,0.7)'
              : '0 24px 64px rgba(15,23,42,0.18)',
          }
        }}
      >
        <DialogTitle sx={{
          p: 0,
          background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.78)} 100%)`,
          color: 'white',
          position: 'relative',
          '&::after': {
            content: '""', position: 'absolute', inset: 0,
            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.14), transparent 60%)',
            pointerEvents: 'none'
          }
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.75,
            px: 3, py: 2.25,
            position: 'relative', zIndex: 1
          }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 1.75,
              backgroundColor: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backdropFilter: 'blur(8px)'
            }}>
              {React.createElement(dialogIcon, { sx: { fontSize: 26 } })}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontSize: '1.2rem', fontWeight: 700,
                letterSpacing: '-0.01em', lineHeight: 1.15
              }}>
                {dialogTitle}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', opacity: 0.9 }}>
                {dialogSubtitle}
              </Typography>
            </Box>
            {!isCheckingStatus && (
              <IconButton onClick={handleClose} sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.18)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' }
              }}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, backgroundColor: t.pageBg }}>
          {/* Debt Payment Information Banner */}
          {isDebtPayment && debtDetails && (
            <Box sx={{
              mb: 3, p: 2.25, borderRadius: 2,
              backgroundColor: alpha(t.accent.amber, t.isDark ? 0.10 : 0.06),
              border: `1px solid ${alpha(t.accent.amber, 0.3)}`,
              borderLeft: `4px solid ${t.accent.amber}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1,
                  backgroundColor: alpha(t.accent.amber, t.isDark ? 0.22 : 0.16),
                  color: t.accent.amber,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <DebtIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography sx={{ color: t.text, fontWeight: 700, fontSize: '1rem' }}>
                  Debt Payment Details
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {debtDetails.debtId && (
                  <Box>
                    <Typography sx={{ color: t.textMuted, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>Debt ID</Typography>
                    <Typography sx={{ color: t.text, fontSize: '0.92rem', fontWeight: 600 }}>{debtDetails.debtId}</Typography>
                  </Box>
                )}
                {debtDetails.originalAmount && (
                  <Box>
                    <Typography sx={{ color: t.textMuted, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>Original Amount</Typography>
                    <Typography sx={{ color: t.text, fontSize: '0.92rem', fontWeight: 600 }}>{formatCurrency(debtDetails.originalAmount)}</Typography>
                  </Box>
                )}
                {debtDetails.remainingAmount && (
                  <Box>
                    <Typography sx={{ color: t.textMuted, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>Remaining Amount</Typography>
                    <Typography sx={{ color: t.accent.amber, fontSize: '0.92rem', fontWeight: 700 }}>{formatCurrency(debtDetails.remainingAmount)}</Typography>
                  </Box>
                )}
                {debtDetails.debtDate && (
                  <Box>
                    <Typography sx={{ color: t.textMuted, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>Debt Date</Typography>
                    <Typography sx={{ color: t.text, fontSize: '0.92rem', fontWeight: 600 }}>{new Date(debtDetails.debtDate).toLocaleDateString()}</Typography>
                  </Box>
                )}
              </Box>

              {debtDetails.debtDescription && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ color: t.textMuted, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>Description</Typography>
                  <Typography sx={{ color: t.text, fontSize: '0.92rem' }}>{debtDetails.debtDescription}</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Status Checking Progress */}
          {isCheckingStatus && (
            <Box sx={{
              mb: 3, p: 2.5, borderRadius: 2,
              backgroundColor: alpha(t.accent.primary, t.isDark ? 0.10 : 0.06),
              border: `1px solid ${alpha(t.accent.primary, 0.3)}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1,
                  backgroundColor: alpha(t.accent.primary, t.isDark ? 0.22 : 0.16),
                  color: t.accent.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <AccessTimeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography sx={{ color: t.text, fontWeight: 700, fontSize: '1rem' }}>
                  Verifying Payment
                </Typography>
              </Box>
              <Typography sx={{ color: t.textMuted, fontSize: '0.88rem', mb: 1.25 }}>
                {paymentStatusMessage}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={statusCheckProgress}
                sx={{
                  height: 8, borderRadius: 4,
                  backgroundColor: alpha(t.accent.primary, t.isDark ? 0.18 : 0.14),
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${t.accent.primary} 0%, ${alpha(t.accent.primary, 0.78)} 100%)`,
                    borderRadius: 4
                  }
                }}
              />
              <Typography sx={{ color: t.textMuted, fontSize: '0.78rem', mt: 1, display: 'block' }}>
                This may take up to 2 minutes. Please don't close this window.
              </Typography>
            </Box>
          )}

          {/* Payment Amount Section — the focal point of the dialog */}
          <Box sx={{
            mb: 3,
            p: { xs: 2.5, sm: 3 },
            borderRadius: 2.5,
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(accent, t.isDark ? 0.18 : 0.10)} 0%, ${alpha(accent, t.isDark ? 0.08 : 0.04)} 100%)`,
            border: `1px solid ${alpha(accent, t.isDark ? 0.35 : 0.25)}`,
            position: 'relative', overflow: 'hidden',
            '&::after': {
              content: '""', position: 'absolute', inset: 0,
              background: `radial-gradient(circle at top right, ${alpha(accent, 0.16)}, transparent 60%)`,
              pointerEvents: 'none'
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography sx={{
                color: t.textMuted, fontSize: '0.78rem', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.75
              }}>
                {isDebtPayment ? 'Debt Payment Amount' : 'Payment Amount'}
              </Typography>
              <Typography sx={{
                fontSize: { xs: '2.4rem', sm: '2.8rem' },
                fontWeight: 800,
                color: accent,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                mb: 1.5
              }}>
                {formatCurrency(selectedTotal)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={isDebtPayment ? 'Debt Amount Due' : 'Total Due'}
                  size="small"
                  sx={{
                    background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.82)} 100%)`,
                    color: 'white',
                    fontWeight: 700, fontSize: '0.72rem',
                    boxShadow: `0 4px 12px ${alpha(accent, 0.35)}`
                  }}
                />
                <Chip
                  icon={isDebtPayment
                    ? <DebtIcon sx={{ fontSize: '14px !important', color: `${accent} !important` }} />
                    : <ReceiptIcon sx={{ fontSize: '14px !important', color: `${accent} !important` }} />
                  }
                  label={paymentType === 'partial_debt' ? 'Partial Debt Payment' :
                         isDebtPayment ? 'Debt Payment' : 'Regular Payment'}
                  size="small"
                  sx={{
                    backgroundColor: alpha(accent, t.isDark ? 0.18 : 0.12),
                    color: accent,
                    fontWeight: 700, fontSize: '0.72rem',
                    border: `1px solid ${alpha(accent, 0.3)}`
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Choose Payment Method */}
          {showPaymentMethods && !isCheckingStatus && paymentMethods.length > 0 && (
            <FormControl component="fieldset" sx={{ width: '100%', mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
                <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: t.accent.primary, boxShadow: `0 0 10px ${alpha(t.accent.primary, 0.5)}` }} />
                <Typography sx={{
                  fontWeight: 700, fontSize: '0.92rem',
                  color: t.text, textTransform: 'uppercase', letterSpacing: 0.4
                }}>
                  Choose Payment Method
                </Typography>
              </Box>

              {paymentMethods.length === 1 && (
                <Box sx={{
                  mb: 2, p: 1.5, borderRadius: 1.5,
                  backgroundColor: alpha(t.accent.primary, t.isDark ? 0.10 : 0.06),
                  border: `1px solid ${alpha(t.accent.primary, 0.25)}`,
                  color: t.text, fontSize: '0.85rem'
                }}>
                  Only <strong>{paymentMethods[0].label}</strong> is available for this {isDebtPayment ? 'debt payment' : 'transaction'}.
                </Box>
              )}

              <RadioGroup
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setMobileMoneyError('');
                  if (e.target.value !== 'mobile_money') {
                    setMobileMoneyData({ payeeMobileNumber: '', channel: '' });
                  }
                }}
                sx={{ gap: 1 }}
              >
                {paymentMethods.map((method) => {
                  const IconComponent = method.icon;
                  const isSelected = paymentMethod === method.id;

                  return (
                    <Box
                      key={method.id}
                      sx={{
                        border: `1.5px solid ${isSelected ? alpha(t.accent.primary, 0.5) : t.borderSubtle}`,
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        backgroundColor: isSelected
                          ? alpha(t.accent.primary, t.isDark ? 0.14 : 0.08)
                          : t.surface,
                        backgroundImage: 'none',
                        '&:hover': {
                          borderColor: alpha(t.accent.primary, isSelected ? 0.6 : 0.35),
                          backgroundColor: isSelected
                            ? alpha(t.accent.primary, t.isDark ? 0.18 : 0.10)
                            : alpha(t.accent.primary, t.isDark ? 0.06 : 0.04),
                        },
                        p: 1.75,
                      }}
                    >
                      <FormControlLabel
                        value={method.id}
                        control={
                          <Radio
                            sx={{
                              color: isSelected ? t.accent.primary : t.textMuted,
                              '&.Mui-checked': { color: t.accent.primary },
                            }}
                            disabled={paymentMethods.length === 1}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <Box sx={{
                              width: 38, height: 38, borderRadius: 1.5,
                              backgroundColor: isSelected
                                ? t.accent.primary
                                : alpha(t.text, t.isDark ? 0.06 : 0.04),
                              color: isSelected ? 'white' : t.textMuted,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              boxShadow: isSelected ? `0 4px 12px ${alpha(t.accent.primary, 0.35)}` : 'none'
                            }}>
                              <IconComponent sx={{ fontSize: 20 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{
                                fontSize: '0.95rem',
                                fontWeight: isSelected ? 700 : 600,
                                color: isSelected ? t.accent.primary : t.text,
                                lineHeight: 1.2
                              }}>
                                {method.label}
                              </Typography>
                              <Typography sx={{ fontSize: '0.78rem', color: t.textMuted, mt: 0.25 }}>
                                {method.description}
                              </Typography>
                            </Box>
                            {isSelected && (
                              <CheckCircleIcon sx={{ color: t.accent.emerald, fontSize: 22 }} />
                            )}
                          </Box>
                        }
                        sx={{ margin: 0, width: '100%' }}
                      />

                      {/* Mobile Money Details */}
                      {method.id === 'mobile_money' && isSelected && allowedPaymentMethods.includes('mobile_money') && (
                        <Box sx={{ mt: 2, pl: { xs: 0, sm: 0.5 } }}>
                          <Box sx={{
                            display: 'flex', justifyContent: 'center',
                            gap: 0.4, mb: 1.5, flexWrap: 'nowrap'
                          }}>
                            {Array.from({ length: 10 }).map((_, index) => (
                              <TextField
                                key={index}
                                value={mobileMoneyDigits[index] || ''}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, '');
                                  if (!val) return;
                                  const newDigits = [...mobileMoneyDigits];
                                  newDigits[index] = val[val.length - 1];
                                  setMobileMoneyDigits(newDigits);
                                  if (index < 9) document.getElementById(`digit-${index + 1}`)?.focus();
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Backspace' && !mobileMoneyDigits[index] && index > 0) {
                                    document.getElementById(`digit-${index - 1}`)?.focus();
                                  }
                                }}
                                inputProps={{
                                  maxLength: 1,
                                  style: {
                                    textAlign: 'center',
                                    fontSize: '1rem',
                                    width: '1.3rem',
                                    height: '1.6rem'
                                  }
                                }}
                                id={`digit-${index}`}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: t.fieldBg,
                                    borderRadius: 1,
                                    '& fieldset': { borderColor: t.borderSubtle },
                                    '&:hover fieldset': { borderColor: alpha(t.accent.primary, 0.5) },
                                    '&.Mui-focused fieldset': { borderColor: t.accent.primary }
                                  }
                                }}
                              />
                            ))}
                          </Box>
                          <Typography sx={{ color: t.textMuted, fontSize: '0.75rem', textAlign: 'center' }}>
                            Enter your 10-digit mobile money number
                          </Typography>

                          <Box sx={{ mt: 1, textAlign: 'center' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setMobileMoneyDigits(Array(10).fill(''))}
                              sx={{
                                textTransform: 'none', fontWeight: 600,
                                color: t.textMuted, borderColor: t.borderStrong,
                                '&:hover': { borderColor: t.borderStrong, backgroundColor: alpha(t.text, 0.04) }
                              }}
                            >
                              Clear Number
                            </Button>
                          </Box>

                          <Typography sx={{ color: t.text, fontSize: '0.85rem', fontWeight: 600, mt: 2, mb: 1 }}>
                            Select your mobile money provider:
                          </Typography>

                          <RadioGroup
                            row
                            value={mobileMoneyData.channel}
                            onChange={handleMobileMoneyChannelChange}
                            sx={{ gap: 1.25, justifyContent: 'center' }}
                          >
                            {mobileMoneyChannels.map((channel) => {
                              const isChannelSelected = mobileMoneyData.channel === channel.id;
                              return (
                                <FormControlLabel
                                  key={channel.id}
                                  value={channel.id}
                                  control={
                                    <Radio sx={{
                                      color: isChannelSelected ? t.accent.primary : t.textMuted,
                                      '&.Mui-checked': { color: t.accent.primary }
                                    }} />
                                  }
                                  label={<Typography sx={{ color: t.text, fontSize: '0.88rem', fontWeight: isChannelSelected ? 700 : 500 }}>{channel.label}</Typography>}
                                  sx={{
                                    border: `1.5px solid ${isChannelSelected ? alpha(t.accent.primary, 0.5) : t.borderSubtle}`,
                                    borderRadius: 1.5,
                                    px: 1.75, py: 0.5,
                                    backgroundColor: isChannelSelected ? alpha(t.accent.primary, t.isDark ? 0.14 : 0.08) : t.surface,
                                    flex: '1 1 auto', m: 0,
                                    justifyContent: 'center'
                                  }}
                                />
                              );
                            })}
                          </RadioGroup>

                          {mobileMoneyError && (
                            <Box sx={{
                              mt: 2, p: 1.25, borderRadius: 1.5,
                              backgroundColor: alpha(t.accent.danger, t.isDark ? 0.12 : 0.08),
                              border: `1px solid ${alpha(t.accent.danger, 0.3)}`,
                              color: t.accent.danger, fontSize: '0.85rem'
                            }}>
                              {mobileMoneyError}
                            </Box>
                          )}

                          <Box sx={{
                            mt: 2, p: 1.75, borderRadius: 1.5,
                            backgroundColor: alpha(t.accent.amber, t.isDark ? 0.10 : 0.06),
                            border: `1px solid ${alpha(t.accent.amber, 0.25)}`
                          }}>
                            <Typography sx={{ color: t.accent.amber, fontSize: '0.85rem', fontWeight: 700, mb: 0.5 }}>
                              Mobile Money {isDebtPayment ? 'Debt ' : ''}Payment Process:
                            </Typography>
                            <Typography component="div" sx={{ color: t.text, fontSize: '0.78rem', lineHeight: 1.6 }}>
                              1. Enter your number<br />
                              2. Select your provider<br />
                              3. Click "Process {isDebtPayment ? 'Debt ' : ''}Payment"<br />
                              4. Approve prompt on your phone<br />
                              5. Enter PIN to complete
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}

          {/* No payment methods available */}
          {showPaymentMethods && paymentMethods.length === 0 && !isCheckingStatus && (
            <Box sx={{
              mb: 3, p: 2, borderRadius: 1.75,
              backgroundColor: alpha(t.accent.amber, t.isDark ? 0.12 : 0.06),
              border: `1px solid ${alpha(t.accent.amber, 0.3)}`,
              color: t.accent.amber, fontSize: '0.88rem'
            }}>
              No payment methods are currently available for this {isDebtPayment ? 'debt payment' : 'transaction'}. Please contact support for assistance.
            </Box>
          )}

          {/* Payment method pre-selected */}
          {!showPaymentMethods && !isCheckingStatus && (
            <Box sx={{
              mb: 3, p: 2.25, borderRadius: 2, textAlign: 'center',
              backgroundColor: t.surfaceAlt,
              border: `1px solid ${t.borderSubtle}`
            }}>
              <Typography sx={{ color: t.textMuted, fontSize: '0.92rem' }}>
                Payment method has been pre-selected for this {isDebtPayment ? 'debt payment' : 'transaction'}.
              </Typography>
              <Chip
                label={allPaymentMethods.find(m => m.id === paymentMethod)?.label || paymentMethod}
                sx={{
                  mt: 1, fontWeight: 700,
                  background: `linear-gradient(135deg, ${t.accent.primary} 0%, ${alpha(t.accent.primary, 0.82)} 100%)`,
                  color: 'white'
                }}
              />
            </Box>
          )}

          {/* Security Notice */}
          {showSecurityNotice && (
            <Box sx={{
              mt: 2.5, p: 1.75, borderRadius: 1.75,
              backgroundColor: alpha(t.accent.emerald, t.isDark ? 0.10 : 0.06),
              border: `1px solid ${alpha(t.accent.emerald, 0.25)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <SecurityIcon sx={{ fontSize: 16, color: t.accent.emerald }} />
                <Typography sx={{ color: t.accent.emerald, fontSize: '0.85rem', fontWeight: 700 }}>
                  Secure Transaction
                </Typography>
              </Box>
              <Typography sx={{ color: t.textMuted, fontSize: '0.78rem' }}>
                Your {isDebtPayment ? 'debt ' : ''}payment is protected by industry-standard encryption and security measures.
                {paymentMethod === 'mobile_money' && ` Mobile money ${isDebtPayment ? 'debt ' : ''}payments are processed directly through your mobile network operator's secure platform.`}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          px: { xs: 2.5, sm: 3.5 }, py: 2,
          gap: 1.25,
          backgroundColor: t.surface,
          borderTop: `1px solid ${t.borderSubtle}`,
          justifyContent: 'flex-end'
        }}>
          {!isCheckingStatus && (
            <>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  minWidth: 110, fontWeight: 600, textTransform: 'none',
                  borderRadius: 1.5,
                  borderColor: t.borderStrong, color: t.text,
                  '&:hover': {
                    borderColor: t.borderStrong,
                    backgroundColor: alpha(t.text, 0.04)
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePaymentSubmit}
                variant="contained"
                disabled={
                  !paymentMethod ||
                  mobileMoneyLoading ||
                  (paymentMethods.length === 0) ||
                  (paymentMethod === 'mobile_money' && enableMobileMoneyValidation && (mobileMoneyDigits.join('').length !== 10 || !mobileMoneyData.channel))
                }
                startIcon={mobileMoneyLoading ?
                  <CircularProgress size={18} color="inherit" /> :
                  <PaymentIcon sx={{ fontSize: 18 }} />
                }
                sx={{
                  minWidth: 160, fontWeight: 700, textTransform: 'none',
                  borderRadius: 1.5, px: 3,
                  background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.82)} 100%)`,
                  boxShadow: `0 6px 16px ${alpha(accent, 0.35)}`,
                  '&:hover': {
                    boxShadow: `0 8px 22px ${alpha(accent, 0.45)}`
                  },
                  '&.Mui-disabled': {
                    background: alpha(t.text, 0.08),
                    color: t.textDim,
                    boxShadow: 'none'
                  }
                }}
              >
                {mobileMoneyLoading
                  ? `Processing${isDebtPayment ? ' Debt' : ''}…`
                  : paymentMethod === 'mobile_money'
                    ? `Send Request`
                    : `Process ${isDebtPayment ? 'Debt ' : ''}Payment`}
              </Button>
            </>
          )}

          {isCheckingStatus && (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Typography sx={{ color: t.textMuted, fontSize: '0.85rem', mb: 1 }}>
                Please wait while we verify your {isDebtPayment ? 'debt ' : ''}payment…
              </Typography>
              <Button
                onClick={() => {
                  Swal.fire({
                    title: `Stop ${isDebtPayment ? 'Debt ' : ''}Payment Verification?`,
                    text: `Are you sure you want to stop the ${isDebtPayment ? 'debt ' : ''}payment verification process?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, Stop',
                    cancelButtonText: 'Continue Waiting'
                  }).then((result) => {
                    if (result.isConfirmed) handleClose();
                  });
                }}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none', fontWeight: 600,
                  borderColor: alpha(t.accent.danger, 0.4),
                  color: t.accent.danger,
                  '&:hover': {
                    borderColor: t.accent.danger,
                    backgroundColor: alpha(t.accent.danger, 0.08)
                  }
                }}
              >
                Cancel Verification
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Zenith GlobalPay MoMo — opens when the cashier picks the
          "Mobile Money (Zenith)" option and confirms. Wallet debit +
          polling live inside; on PAID it calls handleZenithPaid which
          runs the normal charge-marking / day_income insert. */}
      <MomoPaymentDialog
        open={zenithDialogOpen}
        onClose={() => setZenithDialogOpen(false)}
        amount={selectedTotal}
        patient={selectedPatient}
        currentUser={currentUser}
        description={
          isDebtPayment
            ? `Debt payment - ${selectedPatient?.fullName || 'Patient'}`
            : `Hospital bill - ${selectedPatient?.fullName || 'Patient'}`
        }
        onPaid={handleZenithPaid}
      />
    </>
  );
};

export default PaymentDialog;